import { RealtimeChannel, SupabaseClient, REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

import {
  UbiverseNodeType__Child,
  UbiverseNodeType__Parent,
  UbiverseEventTopic,
  UbiverseEventTopicList,
  UbiverseClient,
  UbiverseClientKind,
  UbiverseNodeType,
} from '../types';

const PRESENCE_HEARTBEAT_MS = 30_000;
const RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * Channel naming convention (see docs/device-auth.md):
 *   ubiverse:<user_uuid>         per-account (auth required)
 *   ubiverse:sim:<session_uuid>  anonymous (guest) browser simulator
 *
 * An authenticated /sim session joins the per-account channel — that is what
 * makes it visible to the dashboard's SimulatorNode and lets it emulate a
 * firmware device end-to-end. Guests stay on the isolated sim channel.
 */
const channelNameForUser = (userId: string) => `ubiverse:${userId}`;
const channelNameForSim  = (sessionId: string) => `ubiverse:sim:${sessionId}`;

export class RealtimeClient {
  private supabase: SupabaseClient;
  private channel: RealtimeChannel | null = null;
  private channelName: string | null = null;
  private clientId: string;
  private clientType: UbiverseNodeType;
  private listeners: Map<string, (payload: any) => void> = new Map();
  private presenceCallback: ((clients: UbiverseClient[]) => void) | null = null;
  private userId?: string;
  private isAuthenticated = false;
  private isSubscribed = false;
  private destroyed = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = RECONNECT_DELAY_MS;
  private presenceData: Record<string, any> = {};
  // Presence identity: what this client announces itself as on the channel.
  // The /sim page flips kind to 'device' (with a hardware_id) to emulate a
  // physical ESP32; everything else defaults per route.
  private presenceKind: UbiverseClientKind | null = null;
  private presenceHardwareId: string | null = null;

  constructor(supabase: SupabaseClient) {
    this.supabase = supabase;
    this.clientId = uuidv4();
    this.clientType = window.document.location.pathname === '/sim'
      ? UbiverseNodeType__Child
      : UbiverseNodeType__Parent;
  }

  /**
   * Connect to the appropriate channel:
   *   - signed in (any route)  → per-account ubiverse:<userId> channel
   *   - /sim route, signed out → anonymous ubiverse:sim:<clientId> channel
   *   - other routes, signed out → no channel (auth-gated)
   *
   * Calling connect a second time tears down the previous channel and
   * rebinds. Safe to call on auth changes.
   */
  connect(userId: string | null) {
    if (this.destroyed) return;
    const isSimRoute = window.document.location.pathname === '/sim';

    let nextChannelName: string | null;
    if (userId) {
      nextChannelName = channelNameForUser(userId);
    } else if (isSimRoute) {
      nextChannelName = channelNameForSim(this.clientId);
    } else {
      nextChannelName = null; // signed out on dashboard — stay disconnected
    }

    if (nextChannelName === this.channelName && (this.channel || nextChannelName === null)) {
      return; // already in the right state
    }
    this.cleanup();
    this.channelName = nextChannelName;
    if (nextChannelName) this.openChannel();
  }

  private openChannel() {
    if (this.destroyed || !this.channelName) return;

    const channel = this.supabase.channel(this.channelName, {
      config: {
        broadcast: { self: true },
        presence: { key: this.clientId },
      },
    });

    channel.on('broadcast', { event: '*' }, (message) => {
      const { event, payload } = message;
      if (payload?.targetId && payload.targetId !== this.clientId) return;

      const listener = this.listeners.get(event);
      if (listener) {
        const { targetId, ...rest } = payload || {};
        listener(rest);
      }
    });

    channel.on('presence', { event: 'sync' }, () => this.handlePresenceSync());
    channel.on('presence', { event: 'join' }, () => this.handlePresenceSync());
    channel.on('presence', { event: 'leave' }, () => this.handlePresenceSync());

    channel.subscribe((status, err) => {
      if (this.destroyed) return;

      if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
        console.log(`Realtime connected to ${this.channelName}.`);
        this.isSubscribed = true;
        this.reconnectDelay = RECONNECT_DELAY_MS;
        this.trackPresence();
        this.startHeartbeat();
      } else if (
        status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
        status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT ||
        status === REALTIME_SUBSCRIBE_STATES.CLOSED
      ) {
        console.warn(`Realtime channel ${status}`, err?.message || '');
        this.isSubscribed = false;
        this.stopHeartbeat();
        this.scheduleReconnect();
      }
    });

    this.channel = channel;
  }

  private cleanup() {
    this.stopHeartbeat();
    this.isSubscribed = false;
    if (this.channel) {
      this.supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }

  private scheduleReconnect() {
    if (this.destroyed || this.reconnectTimer) return;
    console.log(`Realtime reconnecting in ${this.reconnectDelay}ms...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openChannel();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.isSubscribed) this.trackPresence();
    }, PRESENCE_HEARTBEAT_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private async trackPresence() {
    if (!this.channel || !this.isSubscribed) return;
    // Child clients default to 'simulator'; the firmware tracks kind:'device'
    // with its hardware_id, and /sim can announce the same via
    // setPresenceIdentity() to emulate one. Parent (dashboard) clients carry
    // no kind — they are not selectable endpoints.
    const kind = this.presenceKind
      ?? (this.clientType === UbiverseNodeType__Child ? 'simulator' : undefined);
    try {
      await this.channel.track({
        clientId: this.clientId,
        type: this.clientType,
        kind,
        hardware_id: this.presenceHardwareId ?? undefined,
        auth: { userId: this.userId, isAuthenticated: this.isAuthenticated },
        data: this.presenceData,
      });
    } catch (err) {
      console.warn('Failed to track presence:', err);
    }
  }

  private handlePresenceSync() {
    if (!this.channel) return;
    const presenceState = this.channel.presenceState();
    const clients: UbiverseClient[] = [];

    for (const key of Object.keys(presenceState)) {
      const presences = presenceState[key] as any[];
      for (const presence of presences) {
        clients.push({
          id: presence.clientId || key,
          type: presence.type,
          kind: presence.kind,
          hardware_id: presence.hardware_id,
          auth: presence.auth,
          data: presence.data,
        });
      }
    }

    if (this.presenceCallback) this.presenceCallback(clients);
  }

  /**
   * Updates the Supabase auth state for the underlying client. When a user
   * signs in / out, the channel is torn down and re-opened on the matching
   * per-account or anonymous channel.
   */
  updateAuth(accessToken?: string | null, userId?: string | null) {
    this.userId = userId || undefined;
    this.isAuthenticated = Boolean(accessToken && userId);
    this.connect(userId || null);
  }

  subscribeEvent(topic: UbiverseEventTopic, callback: (...args: any) => void): void {
    if (topic === UbiverseEventTopicList.SessionListChanged) {
      this.presenceCallback = callback;
      this.handlePresenceSync();
      return;
    }
    this.listeners.set(topic, callback);
  }

  unsubscribeEvent(topic: UbiverseEventTopic): void {
    if (topic === UbiverseEventTopicList.SessionListChanged) {
      this.presenceCallback = null;
      return;
    }
    this.listeners.delete(topic);
  }

  emitEvent(topic: UbiverseEventTopic, payload: any = null): void {
    if (!this.channel || !this.isSubscribed) {
      console.warn('Cannot emit: channel not subscribed');
      return;
    }
    this.channel.send({
      type: 'broadcast',
      event: topic,
      payload: payload || {},
    });
  }

  getConnectionId(): string {
    return this.clientId;
  }

  /** Channel this client is currently bound to (null while disconnected). */
  getChannelName(): string | null {
    return this.channelName;
  }

  async updatePresenceData(data: Record<string, any>) {
    this.presenceData = data;
    await this.trackPresence();
  }

  /**
   * Announce this client as a device (with a hardware_id) or as a simulator
   * on the channel roster. Used by /sim to emulate the firmware's presence
   * behaviour so presence-driven UI (Devices page, SimulatorNode) can be
   * tested without an ESP32.
   */
  async setPresenceIdentity(kind: UbiverseClientKind | null, hardwareId: string | null = null) {
    this.presenceKind = kind;
    this.presenceHardwareId = hardwareId;
    await this.trackPresence();
  }

  destroy() {
    this.destroyed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.cleanup();
    this.listeners.clear();
    this.presenceCallback = null;
  }
}
